/**
 * Whose a session's title is, and which title to show (RFC 0311 3b).
 *
 * Pure reads over a registry entry, kept apart from the mutations so the
 * projections in reads.ts can use them without importing the write side.
 */

import type { SessionMetaEntry, SessionTitleSetBy } from './types'

/** Who wrote a registry title. Absent is `'you'`: an old title may be a person's. */
export function titleSetBy(
  entry: Pick<SessionMetaEntry, 'titleSetBy'> | undefined,
): SessionTitleSetBy {
  const v = entry?.titleSetBy == null ? undefined : String(entry.titleSetBy)
  return v === 'stub' || v === 'agent' ? v : 'you'
}
/** Still a placeholder that nobody has named: the only state an agent names. */
export function awaitingName(
  entry: Pick<SessionMetaEntry, 'titleSetBy' | 'agentTitle'> | undefined,
): boolean {
  return (
    !!entry && titleSetBy(entry) === 'stub' && !String(entry.agentTitle ?? '')
  )
}
/**
 * The title to show for a registry entry (RFC 0311 3b).
 *
 * A person's title wins, always. Otherwise the agent's name, if it gave one,
 * over the stub. This is the read half of the rule `nameSessionOnce` relies on.
 */
export function sessionTitleOf(
  entry:
    | Pick<SessionMetaEntry, 'title' | 'titleSetBy' | 'agentTitle'>
    | undefined,
): string {
  const title = entry?.title == null ? '' : String(entry.title)
  if (!entry || titleSetBy(entry) === 'you') return title
  const agent = entry.agentTitle == null ? '' : String(entry.agentTitle)
  return agent || title
}
